
import Vue, { onBeforeUnmount } from "vue";
import VueFormulate from "@braid/vue-formulate";
import { it, es, en, fr, de, ru, zh } from "@braid/vue-formulate-i18n";

import { ref, onMounted, watch } from "vue";
import { useContext } from "@nuxtjs/composition-api";
import { useHeaderToolbar } from "@/composables/useHeaderToolbar";
import { throttledWatch, useElementSize } from "@vueuse/core";
import { useDataLayer } from "@/composables/useDataLayer";
export default {
  meta: [{ charset: "utf-8" }],
  head() {
    const meta = [];
    if (this.$store.state.staging) {
      meta.push({ hid: "robots", name: "noindex", content: "" });
    }

    return {
      htmlAttrs: {
        lang: this.$store.state.lang,
      },
      meta: meta,
    };
  },
  setup(props) {
    const { params, app } = useContext();

    Vue.use(VueFormulate, {
      plugins: [it, es, en, fr, de, ru, zh],
      locale: params.value.locale
        ? `${params.value.locale.split("-")[0]}`
        : "it",
      validationNameStrategy: ["validationName", "label"],
    });

    const { setToolbar, getToolbar, getTargetToolbarFromCurrentPath } =
      useHeaderToolbar();

    const appCurtain = ref(null);
    const showMenu = ref(false);
    const isVisible = ref(null);
    const { pushDataLayer } = useDataLayer();
    let percentTracker = {
      25: false,
      50: false,
      75: false,
      90: false,
    };

    /* PAGE LEAVE TRANSITIONS */
    function pageBeforeLeave(event) {
      showMenu.value = false;
      if (getToolbar() !== getTargetToolbarFromCurrentPath()) {
        setToolbar(false);
      }
    }

    function pageLeave(pageEvent) {
      ScrollTrigger.normalizeScroll(false);
      window.addEventListener("scrollBehavior", scrollBehavior);
      function scrollBehavior(event) {
        curtainShowing = true;
        appCurtain.value.show(() => {
          curtainShowing = false;
          pageEvent.done();
          hideCurtainOnError();
        });
        // same reference as addEventListener: a .bind() copy would never be removed
        window.removeEventListener("scrollBehavior", scrollBehavior);
      }
    }

    // The error page is rendered without <transition>, so no pageEnter will hide
    // the curtain. The error can be set before or after the leave completes:
    // check on both sides, but never interrupt a running show() (it owns pageEvent.done).
    let curtainShowing = false;
    function hideCurtainOnError() {
      if (app.nuxt.err && !curtainShowing) {
        appCurtain.value.hide();
      }
    }
    watch(() => app.nuxt.err, hideCurtainOnError);

    function pageAfterLeave(event) {}

    function pageLeaveCancelled(event) {}
    /* end PAGE LEAVE TRANSITIONS *

    /* PAGE ENTER TRANSITIONS */
    function pageBeforeEnter(event) {
      ScrollTrigger.getAll().forEach((t) => t.kill());
    }

    function pageEnter(event) {
      percentTracker = {
        25: false,
        50: false,
        75: false,
        90: false,
      };
      event.done();
      appCurtain.value.hide();
    }

    function pageAfterEnter(event) {
      setToolbar(getTargetToolbarFromCurrentPath());

      if (getToolbar()) {
        patchPagePadding(event.el);
      }

      setTimeout(() => {
        ScrollTrigger.refresh();
        ScrollTrigger.normalizeScroll(/*true*/);
      }, 100);
    }

    function pageEnterCancelled(event) {}
    /* end PAGE ENTER TRANSITIONS */
    function onScroll() {
      const perc = Math.round(
        (window.scrollY /
          (document.documentElement.scrollHeight - window.innerHeight)) *
          100
      );
      if (perc >= 90 && percentTracker[90] === false) {
        pushDataLayer({
          event: "scroll_90",
        });
        percentTracker[90] = true;
      } else if (perc >= 75 && percentTracker[75] === false) {
        pushDataLayer({
          event: "scroll_75",
        });
        percentTracker[75] = true;
      } else if (perc >= 50 && percentTracker[50] === false) {
        pushDataLayer({
          event: "scroll_50",
        });
        percentTracker[50] = true;
      } else if (perc >= 25 && percentTracker[25] === false) {
        pushDataLayer({
          event: "scroll_25",
        });
        percentTracker[25] = true;
      }
    }

    onBeforeUnmount(() => {
      window.removeEventListener("scroll", onScroll);
    });

    onMounted(() => {
      setToolbar(getTargetToolbarFromCurrentPath());

      if (getToolbar()) {
        patchPagePadding(document.getElementsByClassName("component-page")[0]);
      }

      ScrollTrigger.normalizeScroll(/*true*/);
      window.addEventListener("scroll", onScroll);
    });
    // PATCH PAGE TOP SPACING
    function patchPagePadding(target) {
      target.classList.add("has-toolbar");
    }

    const tpl_mainContainer = ref(null);
    const { height: containerHeight } = useElementSize(tpl_mainContainer);

    throttledWatch(
      containerHeight,
      (value) => {
        ScrollTrigger.refresh(true);
      },
      { throttle: 1000, trailing: true, leading: false }
    );

    return {
      showMenu,
      isVisible,
      appCurtain,
      tpl_mainContainer,
      pageBeforeEnter,
      pageEnter,
      pageAfterEnter,
      pageEnterCancelled,
      pageBeforeLeave,
      pageLeave,
      pageAfterLeave,
      pageLeaveCancelled,
    };
  },
};
